/* eslint-disable no-shadow */
import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import { CircularProgress } from '@material-ui/core';
import { DataGrid, GridColDef, GridRowParams } from '@material-ui/data-grid';
import debounce from 'lodash/debounce';
import { BiSearch } from 'react-icons/bi';
import { BsCheck } from 'react-icons/bs';
import { RiCloseLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import api from '~/services/api';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';

import {
  StyledModal,
  HeaderBar,
  Title,
  ControlsRow,
  SegmentedGroup,
  SegmentedBtn,
  SearchWrap,
  SearchInput,
  GridWrap,
  FooterBar,
  StatusAssistive,
} from './styles';
import { upperCase } from 'lodash';

interface FormProps {
  showModal: boolean;
  value: string | number | undefined;
  onChange: (data: any, data1: boolean, codConta: number) => any;
  setShowModal: (val: boolean) => void;
  setConta: (val: any) => void;
}

type Contas = {
  id: number;
  conta_contabil: string;
  conta_reduzida?: string;
  des_conta_contabil: string;
};

enum ContaFiltro {
  ContaContabil = 0,
  Reduzida = 1,
  Descricao = 2,
}

/**
 * 🧩 ModalContabil
 * Modal de seleção de Conta Contábil com busca, filtro por critério e grid.
 *
 * Acessibilidade:
 * - Foco no campo de busca ao abrir;
 * - Enter confirma quando há linha selecionada;
 * - Ícone de carregamento com aria-live.
 *
 * Uso:
 * <ModalContabil showModal ... onChange ... />
 */
export const ModalContabil: React.FC<FormProps> = ({
  setShowModal,
  showModal,
  onChange,
  setConta,
}) => {
  const [tipoFiltro, setTipoFiltro] = useState<ContaFiltro>(
    ContaFiltro.ContaContabil,
  );
  const [filtroRaw, setFiltroRaw] = useState<string>('');
  const [filtro, setFiltro] = useState<string>('');
  const [selectedConta, setSelectedConta] = useState<Contas | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [contas, setContas] = useState<Contas[]>([]);

  // Debounce para evitar chamadas excessivas na digitação
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedApplyFilter = useMemo(
    () => debounce((val: string) => setFiltro(val.trim()), 300),
    [],
  );

  useEffect(() => {
    if (showModal) {
      setLoading(true);
      handleContasFiltros();
    } else {
      setFiltroRaw('');
      setFiltro('');
      setTipoFiltro(ContaFiltro.ContaContabil);
      setContas([]);
      setSelectedConta(null);
    }
  }, [showModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleContasFiltros();
  }, [filtro, tipoFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Busca/filtra contas via API
   */
  const handleContasFiltros = useCallback(async () => {
    try {
      if (String(filtro).length >= 1) setLoading(true);

      const escapedFilter = filtro.includes("'")
        ? filtro.replace(/'/g, "''")
        : filtro;
      const { data } = await api.get(
        `contaContabil/conta/${tipoFiltro}?campo=${upperCase(escapedFilter)}`,
      );

      if (data?.success) {
        const contasFormatadas: Contas[] = (data.data || []).map((el: any) => ({
          id: el.cod_conta,
          conta_contabil: el.conta_contabil,
          conta_reduzida: el.conta_reduzida,
          des_conta_contabil: el.des_conta_contabil,
        }));
        setContas(contasFormatadas);
      } else {
        setContas([]);
      }
    } catch {
      setContas([]);
    } finally {
      setLoading(false);
    }
  }, [filtro, tipoFiltro]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80, hide: true },
      { field: 'conta_contabil', headerName: 'Conta Contábil', width: 200 },
      { field: 'conta_reduzida', headerName: 'Reduzida', width: 150 },
      {
        field: 'des_conta_contabil',
        headerName: 'Descrição',
        width: 520,
        headerClassName: 'hideRightSeparator',
        flex: 1,
      },
    ],
    [],
  );

  const onRowClick = (param: GridRowParams) => {
    const { row } = param;
    if (!row) {
      return toast.warning(
        'Clique sobre a Conta Contábil que deseja selecionar!',
      );
    }
    setSelectedConta(row as Contas);
  };

  const handleConfirm = useCallback(() => {
    if (selectedConta?.id) {
      const payload = {
        cod_conta: selectedConta.id,
        conta_contabil: selectedConta.conta_contabil,
        conta_reduzida: selectedConta.conta_reduzida,
        des_conta_contabil: selectedConta.des_conta_contabil,
      };
      setConta(payload);
      onChange(selectedConta.conta_reduzida, false, selectedConta.id);
      setContas([]);
      setShowModal(false);
      toast.success(
        `Conta Contábil ${
          selectedConta.conta_reduzida ?? selectedConta.id
        } foi adicionada`,
      );
    } else {
      toast.warning('Clique sobre a Conta Contábil que deseja selecionar!');
    }
  }, [onChange, selectedConta, setConta, setShowModal]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleConfirm();
  };

  const resetModalState = useCallback(() => {
    setFiltroRaw('');
    setFiltro('');
    setTipoFiltro(ContaFiltro.ContaContabil);
    setContas([]);
    setSelectedConta(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetModalState();
    setShowModal(false);
  }, [resetModalState, setShowModal]);

  return (
    <>
      <StyledModal
        show={showModal}
        onHide={handleClose}
        centered
        size="xl"
        aria-labelledby="titulo-modal-contabil"
        contentClassName="kvx-modal-content"
        dialogClassName="kvx-modal-dialog"
      >
        <HeaderBar>
          <Title id="titulo-modal-contabil">Selecione a Conta Contábil</Title>

          <CustomButtonNovo
            aria-label="Fechar modal"
            onClick={handleClose}
            type="button"
            icon={RiCloseLine}
            iconOnly
            width="40px"
            ghost
          />
        </HeaderBar>

        <Modal.Body>
          <ControlsRow>
            <SegmentedGroup role="tablist" aria-label="Filtro de busca">
              <SegmentedBtn
                role="tab"
                aria-selected={tipoFiltro === ContaFiltro.ContaContabil}
                $active={tipoFiltro === ContaFiltro.ContaContabil}
                onClick={() => setTipoFiltro(ContaFiltro.ContaContabil)}
              >
                Conta Contábil
              </SegmentedBtn>

              <SegmentedBtn
                role="tab"
                aria-selected={tipoFiltro === ContaFiltro.Reduzida}
                $active={tipoFiltro === ContaFiltro.Reduzida}
                onClick={() => setTipoFiltro(ContaFiltro.Reduzida)}
              >
                Reduzida
              </SegmentedBtn>

              <SegmentedBtn
                role="tab"
                aria-selected={tipoFiltro === ContaFiltro.Descricao}
                $active={tipoFiltro === ContaFiltro.Descricao}
                onClick={() => setTipoFiltro(ContaFiltro.Descricao)}
              >
                Descrição
              </SegmentedBtn>
            </SegmentedGroup>

            <SearchWrap>
              <BiSearch aria-hidden="true" />
              <SearchInput
                type="text"
                placeholder="Pesquisar..."
                value={filtroRaw}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value;
                  setFiltroRaw(val);
                  debouncedApplyFilter(val);
                }}
                onKeyDown={onKeyDown}
                autoFocus
                aria-label="Pesquisar contas"
              />
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                <span aria-hidden="true" className="placeholder-end" />
              )}
            </SearchWrap>

            <StatusAssistive aria-live="polite">
              {loading
                ? 'Carregando resultados…'
                : `${contas.length} resultado(s)`}
            </StatusAssistive>
          </ControlsRow>

          <GridWrap>
            <DataGrid
              className="kvx-grid"
              rows={contas}
              columns={columns}
              pageSize={8}
              rowsPerPageOptions={[8]}
              sortModel={[{ field: 'des_conta_contabil', sort: 'asc' }]}
              onRowClick={onRowClick}
              autoHeight
              disableColumnMenu
              disableSelectionOnClick
              loading={loading}
              localeText={{
                noRowsLabel: 'Nenhum registro encontrado...',
                columnMenuLabel: 'Menu',
                columnMenuFilter: 'Filtrar',
                columnMenuHideColumn: 'Esconder',
                columnMenuUnsort: 'Não ordenar',
                columnMenuSortAsc: 'Ordernar ASC',
                columnMenuSortDesc: 'Ordernar DESC',
                columnMenuShowColumns: 'Mostrar colunas',
              }}
              getRowClassName={(params) =>
                params.id === selectedConta?.id
                  ? 'kvx-row kvx-row--selected'
                  : 'kvx-row'
              }
            />
          </GridWrap>

          <FooterBar>
            <CustomButtonNovo
              onClick={handleConfirm}
              label="Selecionar"
              icon={BsCheck}
              variant="confirm"
              disabled={!selectedConta?.id}
            />
          </FooterBar>
        </Modal.Body>
      </StyledModal>
    </>
  );
};
